import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "../App";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Trash2, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import {
  getGuestCart,
  saveGuestCart,
  clearGuestCart,
} from "../utils/guestCart";

export default function CartPage() {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  // logged-in cart (from backend)
  const [cart, setCart]       = useState(null);
  // guest cart (from localStorage)
  const [guestItems, setGuestItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Load cart ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setGuestItems(getGuestCart());
      setLoading(false);
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
    } catch {
      toast.error("Failed to load cart");
    } finally {
      setLoading(false);
    }
  };

  // ── Logged-in: update quantity ────────────────────────────────────────────
  const updateQuantity = async (productId, newQty) => {
    if (newQty < 1) return;
    try {
      await axios.put(`${API}/cart/${productId}?quantity=${newQty}`, {}, { withCredentials: true });
      fetchCart();
    } catch { toast.error("Failed to update quantity"); }
  };

  // ── Logged-in: remove item ────────────────────────────────────────────────
  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${productId}`, { withCredentials: true });
      toast.success("Item removed");
      fetchCart();
    } catch { toast.error("Failed to remove item"); }
  };

  // ── Guest: update quantity in localStorage ────────────────────────────────
  const updateGuestQty = (productId, newQty) => {
    if (newQty < 1) return;
    const updated = guestItems.map(i =>
      i.product_id === productId ? { ...i, quantity: newQty } : i
    );
    saveGuestCart(updated);
    setGuestItems(updated);
  };

  // ── Guest: remove item from localStorage ──────────────────────────────────
  const removeGuestItem = (productId) => {
    const updated = guestItems.filter(i => i.product_id !== productId);
    saveGuestCart(updated);
    setGuestItems(updated);
    toast.success("Item removed");
  };

  // ── Unified list and total ────────────────────────────────────────────────
  const cartItems = user ? (cart?.items || []) : guestItems;

  const calculateTotal = () =>
    cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent" />
      </div>
    );
  }

  const isEmpty = cartItems.length === 0;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 px-6 md:px-12 max-w-7xl mx-auto py-12">
        <h1
          className="cormorant text-4xl sm:text-5xl font-light tracking-tight mb-12"
          data-testid="cart-title"
        >
          Shopping Cart
        </h1>

        {isEmpty ? (
          <div className="text-center py-12">
            <p className="text-zinc-600 mb-6">Your cart is empty.</p>
            <Link
              to="/shop"
              className="inline-block border border-zinc-900 text-zinc-900 hover:bg-zinc-100 rounded-none px-8 py-4 uppercase tracking-widest text-xs transition-colors"
              data-testid="continue-shopping-button"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* ── Cart items ──────────────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-6">
              {cartItems.map(item => (
                <div
                  key={item.product_id}
                  className="flex gap-6 pb-6 border-b border-zinc-200"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                    <img
                      src={resolveImageUrl(item.product?.image_url)}
                      alt={item.product?.name}
                      className="w-24 h-24 object-cover bg-zinc-100"
                    />
                  </Link>

                  <div className="flex-1">
                    <Link to={`/product/${item.product_id}`}>
                      <h3 className="text-xl font-light cormorant mb-1">
                        {item.product?.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-zinc-500 mb-3">{item.product?.category}</p>
                    <p className="text-base font-medium">
                      ₹{item.product?.price?.toLocaleString()}
                    </p>

                    <div className="flex items-center gap-4 mt-4">
                      {/* Quantity controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            user
                              ? updateQuantity(item.product_id, item.quantity - 1)
                              : updateGuestQty(item.product_id, item.quantity - 1)
                          }
                          className="w-8 h-8 border border-zinc-300 hover:bg-zinc-100 transition-colors flex items-center justify-center"
                          data-testid={`decrease-quantity-${item.product_id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span
                          className="text-sm w-8 text-center"
                          data-testid={`quantity-${item.product_id}`}
                        >
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            user
                              ? updateQuantity(item.product_id, item.quantity + 1)
                              : updateGuestQty(item.product_id, item.quantity + 1)
                          }
                          className="w-8 h-8 border border-zinc-300 hover:bg-zinc-100 transition-colors flex items-center justify-center"
                          data-testid={`increase-quantity-${item.product_id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() =>
                          user
                            ? removeItem(item.product_id)
                            : removeGuestItem(item.product_id)
                        }
                        className="text-zinc-500 hover:text-red-600 transition-colors"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-lg font-medium">
                      ₹{((item.product?.price || 0) * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Order summary ───────────────────────────────────────── */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-zinc-200 p-6">
                <h2 className="text-xl font-light cormorant mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Subtotal</span>
                    <span data-testid="cart-subtotal">
                      ₹{calculateTotal().toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Shipping</span>
                    <span className="text-green-600">Free</span>
                  </div>
                  <div className="border-t border-zinc-200 pt-3 flex justify-between">
                    <span className="font-medium">Total</span>
                    <span
                      className="text-xl font-medium"
                      data-testid="cart-total"
                    >
                      ₹{calculateTotal().toLocaleString()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => navigate("/checkout")}
                  className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-none px-8 py-4 uppercase tracking-widest text-xs transition-colors mb-4"
                  data-testid="proceed-to-checkout-button"
                >
                  Proceed to Checkout
                </button>

                <Link
                  to="/shop"
                  className="block text-center text-sm text-zinc-600 hover:text-zinc-900"
                  data-testid="continue-shopping-link"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
